import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Typography, Box } from "@mui/material"

import { formatChartNumber } from "./utils/numberFormat"
import OverviewCard, {
  overviewCardValueSx,
  overviewCardHelperTextSx,
} from "./OverviewCard"
import OverviewMetricChip from "./OverviewMetricChip"

const EPS = 0.01

const getChipAccent = deviation => {
  if (deviation > EPS) return "warning"
  if (deviation < -EPS) return "success"
  return "default"
}

const getChipArrow = deviation => {
  if (deviation > EPS) return "up"
  if (deviation < -EPS) return "down"
  return undefined
}

const formatSignedPercentForChip = value => {
  if (value === null || !Number.isFinite(value)) return null
  const abs = Math.abs(value)
  const sign = value > 0 ? "+" : value < 0 ? "−" : ""
  return `${sign}${formatChartNumber(abs, 2)}%`
}

const EacVsObjectiveCard = ({
  costObjective,
  deviation,
  deviationPercent,
  isLoading,
  currency,
}) => {
  const absAmount = formatChartNumber(Math.abs(deviation), 2)
  const amountSign = deviation > 0 ? "+" : deviation < 0 ? "−" : ""
  const amountText = `${amountSign}${absAmount} ${currency ?? ""}`.trim()

  const percentStr = formatSignedPercentForChip(deviationPercent)
  const hasPercentChip = percentStr !== null

  const chipAccent = getChipAccent(deviation)
  const chipArrow = getChipArrow(deviation)

  return (
    <OverviewCard
      iconPreset="eacVsObjective"
      title={i18n.t("overview.eacVsObjectiveCardTitle")}
      infoText={i18n.t("overview.eacVsObjectiveCardTooltip")}
      loading={isLoading}
      sx={{ height: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography sx={overviewCardValueSx}>{amountText}</Typography>
        </Box>
        {hasPercentChip && (
          <OverviewMetricChip
            accent={chipAccent}
            arrow={chipArrow}
            sx={{ alignSelf: "flex-start" }}
          >
            {percentStr}
          </OverviewMetricChip>
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          ...overviewCardHelperTextSx,
          mt: 1.5,
          fontSize: 13,
        }}
      >
        {i18n.t("overview.eacVsObjectiveCardObjectiveLine", {
          amount: formatChartNumber(costObjective, 2),
          currency: currency ?? "",
        })}
      </Typography>
    </OverviewCard>
  )
}

EacVsObjectiveCard.propTypes = {
  costObjective: PropTypes.number.isRequired,
  deviation: PropTypes.number.isRequired,
  deviationPercent: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
}

EacVsObjectiveCard.defaultProps = {
  deviationPercent: null,
  isLoading: false,
  currency: "",
}

export default EacVsObjectiveCard
